import * as THREE from 'three';
import type { ClientPerfSample } from '../net/protocol';
import { isTouchDevice } from '../app/env';

/**
 * Always-on client render telemetry.
 *
 * The `?perf=1` overlay (render/PerfOverlay.ts) answers "why is MY machine slow";
 * this answers "which real players are slow", which is the only way to know
 * whether a render change helped on hardware we don't own. It aggregates in the
 * browser and flushes a single small summary per window — never a per-frame
 * stream, and never anything that identifies the machine (see ClientPerfSample).
 *
 * Cost per frame is one array push and two adds, so it runs unconditionally.
 */

/** Flush roughly this often, so a player who never finishes a match still reports. */
export const PERF_FLUSH_MS = 60_000;
/** Ignore a window this short — usually a tab closing mid-flush; the stats are noise. */
const MIN_FRAMES = 120;
/** Cap retained frame times (~2 min at 144Hz). Beyond this we drop, keeping percentiles honest. */
const MAX_TIMES = 20_000;

export class PerfSampler {
  private times: number[] = [];
  private drawnFrames = 0;
  private callSum = 0;
  private triSum = 0;
  private windowStart = performance.now();
  private gpu: ClientPerfSample['gpu'] | null = null;

  /**
   * Record one loop iteration. `frameMs` is the interval since the previous
   * iteration; `didDraw` distinguishes a real draw from an on-demand skip.
   */
  sample(renderer: THREE.WebGLRenderer, frameMs: number, didDraw: boolean): void {
    // A tab returning from background reports a multi-second "frame" that would
    // dominate p95 and misreport a machine as janky. Drop implausible intervals.
    if (frameMs > 0 && frameMs < 2000 && this.times.length < MAX_TIMES) this.times.push(frameMs);
    if (didDraw) {
      this.drawnFrames++;
      this.callSum += renderer.info.render.calls;
      this.triSum += renderer.info.render.triangles;
    }
  }

  /** True once the window holds enough frames to be worth sending. */
  get ready(): boolean {
    return this.times.length >= MIN_FRAMES;
  }

  /**
   * Build the summary for this window and start a new one. Returns null when the
   * window is too short to mean anything (the window is reset either way, so a
   * discarded window can't leak into the next one).
   */
  flush(renderer: THREE.WebGLRenderer, scene: THREE.Scene, env: string): ClientPerfSample | null {
    const times = this.times;
    const drawn = this.drawnFrames;
    const callSum = this.callSum;
    const triSum = this.triSum;
    const windowMs = performance.now() - this.windowStart;
    const enough = times.length >= MIN_FRAMES;
    this.reset();
    if (!enough) return null;

    times.sort((a, b) => a - b);
    const at = (q: number) => times[Math.min(times.length - 1, Math.floor(times.length * q))];

    let lights = 0;
    scene.traverse((o) => {
      if ((o as THREE.Light).isLight && o.visible) lights++;
    });

    const size = renderer.getDrawingBufferSize(new THREE.Vector2());
    const r2 = (n: number) => Math.round(n * 100) / 100;
    return {
      windowMs: Math.round(windowMs),
      frames: times.length,
      drawn: r2(drawn / times.length),
      medianMs: r2(at(0.5)),
      p95Ms: r2(at(0.95)),
      calls: drawn ? Math.round(callSum / drawn) : 0,
      triangles: drawn ? Math.round(triSum / drawn) : 0,
      programs: renderer.info.programs?.length ?? 0,
      lights,
      pixelRatio: r2(renderer.getPixelRatio()),
      pixels: Math.round(size.x * size.y),
      env,
      gpu: (this.gpu ??= detectGpu(renderer)),
      device: isTouchDevice() ? 'touch' : 'desktop',
    };
  }

  private reset(): void {
    this.times = [];
    this.drawnFrames = 0;
    this.callSum = 0;
    this.triSum = 0;
    this.windowStart = performance.now();
  }
}

/**
 * Reduce the GPU to a vendor bucket, in the browser, before anything is sent.
 * `WEBGL_debug_renderer_info` is itself a fingerprinting surface and some
 * browsers already refuse it — an unavailable string is simply 'other'.
 */
function detectGpu(renderer: THREE.WebGLRenderer): ClientPerfSample['gpu'] {
  let s = '';
  try {
    const gl = renderer.getContext();
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (ext) s = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? '');
  } catch {
    /* blocked or unsupported: fall through to 'other' */
  }
  s = s.toLowerCase();
  if (/nvidia|geforce|quadro|rtx|gtx/.test(s)) return 'nvidia';
  if (/amd|radeon|rx\s|firepro/.test(s)) return 'amd';
  if (/intel|iris|uhd graphics|hd graphics/.test(s)) return 'intel';
  if (/apple|m1|m2|m3/.test(s)) return 'apple';
  if (/adreno|qualcomm/.test(s)) return 'adreno';
  if (/mali|arm/.test(s)) return 'mali';
  return 'other';
}
